import * as React from 'react';
import { KeyboardEvent, useCallback, useRef } from 'react';
import { ThemeDefinition } from '@volkswagen-onehub/components-core';
import styled from 'styled-components';
import {
  LENTIL_GAP,
  LENTIL_SIZE,
  lentilsHeigth,
  lentilsMaxWith,
  getRef,
  lentilsTransform,
  lentilsWidth,
  isSmall,
} from './lentils.helpers';

const StyledLentil = styled.button<{
  $isActive: boolean;
  $isSmall: boolean;
}>`
  position: relative;
  width: ${(props) => LENTIL_SIZE({ theme: props.theme as unknown as ThemeDefinition })};
  height: ${(props) => LENTIL_SIZE({ theme: props.theme as unknown as ThemeDefinition })};
  border: 0;
  padding: 0;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.content.active};
  outline: none;
  transform: scale(
    ${({ $isActive, $isSmall }) => {
      if ($isActive) {
        return 2;
      }
      if ($isSmall) {
        return 0.5;
      }
      return 1;
    }}
  );
  transition: ${({ theme: { animation } }) =>
    `all ${animation.duration.layer} ${animation.timingFunction.in}`};
  cursor: pointer;

  :before {
    content: '';
    position: absolute;
    top: 12.5%;
    left: 12.5%;
    width: 75%;
    height: 75%;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.content.active};
  }

  :hover {
    :before {
      background: ${({ theme }) => theme.colors.button.primary.hover};
    }
  }

  :focus-visible {
    outline: none;

    :before {
      box-shadow: 0 0 0 1px ${({ theme }) => theme.colors.focus.main};
    }
  }
`;

const StyledLentilsLayout = styled.div<{
  length: number;
  $activeIndex: number;
}>`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${(props) => LENTIL_GAP({ theme: props.theme as unknown as ThemeDefinition })};

  transition: ${({ theme: { animation } }) =>
    `all ${animation.duration.layer} ${animation.timingFunction.in}`};

  ${lentilsWidth};
  ${lentilsHeigth};
  ${lentilsTransform};
`;

const StyledLentilsScrollWrapper = styled.div`
  padding: 0
    calc(
      (
          ${(props) => LENTIL_SIZE({ theme: props.theme as unknown as ThemeDefinition })} * 2 -
            ${(props) => LENTIL_SIZE({ theme: props.theme as unknown as ThemeDefinition })}
        ) /
        2
    );
  transition: ${({ theme: { animation } }) =>
    `padding-inline-start ${animation.duration.layer} ${animation.timingFunction.in}`};
  overflow: hidden;
  pointer-events: all;

  ${lentilsMaxWith};
`;

const StyledLentilsContainer = styled.div`
  // padding: ${({ theme }) => `${theme.size.static150} ${theme.size.static250}`};
  //background-color: ${({ theme }) => theme.colors.background.primary};
  border-radius: ${({ theme }) => theme.size.static400};
`;

interface LentilsProps {
  readonly onChange?: (lentilIndex: number) => void;
  readonly ariaControlsId?: string;
  readonly activeIndex: number;
  readonly slideLength: number;
  readonly slideKeys: string[];
}

export const Lentils = ({
  onChange,
  ariaControlsId,
  activeIndex,
  slideLength,
  slideKeys,
}: LentilsProps): React.ReactElement => {
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);

  function handleChange(newIndex: number) {
    if (onChange) {
      onChange(newIndex);
    }
  }

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        handleChange(activeIndex === 0 ? slideLength - 1 : activeIndex - 1);
        prevRef.current?.focus({ preventScroll: true });
      } else if (e.key === 'ArrowRight') {
        handleChange((activeIndex + 1) % slideLength);
        nextRef.current?.focus({ preventScroll: true });
      }
    },
    [activeIndex, slideLength],
  );

  return (
    <StyledLentilsContainer>
      <StyledLentilsScrollWrapper>
        <StyledLentilsLayout length={slideLength} $activeIndex={activeIndex}>
          {Array.from({ length: slideLength }).map((_, index) => (
            <StyledLentil
              key={`media-gallery-lentil-${slideKeys[index]}`}
              ref={getRef(index, activeIndex, slideLength, prevRef, nextRef)}
              $isActive={index === activeIndex}
              onClick={() => handleChange(index)}
              onKeyDown={handleKeyDown}
              tabIndex={activeIndex === index ? 0 : -1}
              role="radio"
              aria-checked={activeIndex === index}
              aria-controls={ariaControlsId}
              data-testid={`media-gallery-lentil-${index}`}
              $isSmall={isSmall(index, activeIndex, slideLength)}
              aria-label={`carousel slide ${index + 1}`}
            />
          ))}
        </StyledLentilsLayout>
      </StyledLentilsScrollWrapper>
    </StyledLentilsContainer>
  );
};
